import { useEffect, useState } from 'react'

import { useFilter, useUrlStateManager } from '@rewardops/ui'
import { AnalyticEvents } from '@rewardops/utils'

import { SearchTypes } from '~/utils/sort-utils'
import { useAnalytics } from './use-analytics'
import { useApi } from './use-api'
import { usePreventBodyScroll } from './use-prevent-body-scroll'

type UpdateSearchParams = {
  query: string
  qType: string
}

export const useSearchBox = () => {
  const { tenant } = useApi()
  const { searchQuery, setSearchQuery, searchType } = useFilter()
  const { storeUrlParams, restoreUrlParams } = useUrlStateManager()
  const analytics = useAnalytics()

  const [isSearchBarOpened, setIsSearchBarOpened] = useState(
    searchType && searchQuery?.length > 0,
  )
  const [isSearchOverlayOpened, setIsSearchOverlayOpened] = useState(false)

  // Disable body scroll and enable overlay scroll when search is open.
  usePreventBodyScroll(!!searchQuery)

  // Combined debounced state for query and qType
  const [debouncedState, setDebouncedState] = useState<UpdateSearchParams>({
    query: searchQuery,
    qType: searchType,
  })

  // Debounce searchQuery updates
  useEffect(() => {
    if (!tenant?.flags?.searchEnabled || !debouncedState.qType) return

    const timer = setTimeout(() => {
      analytics.sendCustomGoogleAnalyticsEvent({
        type: AnalyticEvents.Search,
        data: {
          search_term: debouncedState.query,
          search_type: debouncedState.qType,
        },
      })
      setSearchQuery({
        q: debouncedState.query,
        qType: debouncedState.qType,
      })
    }, 500)

    return () => clearTimeout(timer)
  }, [debouncedState])

  useEffect(() => {
    if (!searchQuery && !searchType) {
      // This condition will close the search bar when user clicks on the item from the search results
      // it's needed because the nav bar is not re-rendered when user clicks on the item from the search results
      closeSearchBar()
      return
    }

    processSearchQuery({ query: searchQuery, qType: searchType })
  }, [searchQuery, searchType])

  const openSearchBar = () => {
    setIsSearchBarOpened(true)
    updateSearchQuery({ query: '', qType: SearchTypes.Products })
  }

  const closeSearchBar = () => {
    setIsSearchBarOpened(false)
    setIsSearchOverlayOpened(false)
  }

  const resetSearch = () => {
    updateSearchQuery({ query: '', qType: '' })
    restoreUrlParams()
  }

  const clearSearchInput = () => {
    // Clean search query but set the current search type to display the searched input opened
    updateSearchQuery({ query: '', qType: searchType })
  }

  // Trigger useEffect to update url params (debouncedState -> searchQuery)
  const updateSearchQuery = ({ query, qType }: UpdateSearchParams) => {
    setDebouncedState({ query, qType })

    if (!query && !qType) {
      closeSearchBar()
    }
  }

  // After url params are updated, process the search query
  const processSearchQuery = ({ query, qType }: UpdateSearchParams) => {
    if (!qType) {
      resetSearch()
      return
    }

    if (!query) {
      handleNoQuery(qType)
      return
    }

    handleValidQuery()
  }

  const handleNoQuery = (qType: string) => {
    if (isSearchOverlayOpened) {
      setIsSearchOverlayOpened(false)
      restoreUrlParams({ overrides: { searchType: qType } })
    }
  }

  const handleValidQuery = () => {
    if (!isSearchOverlayOpened) {
      storeUrlParams({ excludeKeys: ['q', 'searchType'] })
      setIsSearchOverlayOpened(true)
    }
  }

  return {
    isSearchBarOpened,
    openSearchBar,
    debouncedState,
    updateSearchQuery,
    clearSearchInput,
    resetSearch,
  }
}
